<template>
  <Teleport to="body">
    <div
      v-if="dialogVisible"
      class="translate-engine-overlay"
      :class="{ 'is-mobile': isMobile }"
      role="dialog"
      aria-modal="true"
      @click.self="closeDialog"
    >
      <div class="translate-engine-panel" :class="{ 'is-mobile': isMobile }" :style="panelStyle">
        <div v-if="isMobile" class="translate-engine-header">
          <div class="translate-engine-title">{{ t('translate_engine_select') }}</div>
          <button class="translate-engine-close" type="button" @click="closeDialog">
            <el-icon>
              <Close />
            </el-icon>
          </button>
        </div>

        <div class="translate-engine-body">
          <div
            ref="tabsRef"
            class="translate-engine-tabs"
            :class="{ 'is-dragging': tabDragState.dragging }"
            @wheel.prevent="onTabsWheel"
            @pointerdown="onTabsPointerDown"
            @pointermove="onTabsPointerMove"
            @pointerup="onTabsPointerUp"
            @pointercancel="onTabsPointerCancel"
            @pointerleave="onTabsPointerLeave"
          >
            <button
              v-for="group in engineGroups"
              :key="group.key"
              :data-tab-key="group.key"
              class="translate-engine-tab"
              :class="{ 'is-active': group.key === activeTab }"
              type="button"
              @click="onTabClick(group.key, $event)"
            >
              <span class="translate-engine-tab-label">{{ group.label }}</span>
              <span class="translate-engine-tab-badge" :class="group.badgeClass">{{ group.multiplier }}</span>
            </button>
          </div>

          <div class="translate-engine-scroll">
            <div class="engine-list">
              <button
                v-for="item in activeGroup.items"
                :key="getEngineKey(item)"
                class="engine-item"
                :class="{
                  'is-selected': isSelected(item),
                  'is-disabled': isDisabled(item),
                }"
                type="button"
                @click="handleSelect(item)"
              >
                <span class="engine-icon" :style="getIconStyle(item)">
                  <i
                    v-if="item.iconImgUrl"
                    class="iconfont"
                    :class="item.iconImgUrl"
                  />
                  <span v-else>{{ getFallbackIcon(item) }}</span>
                </span>

                <span class="engine-name">{{ getEngineName(item) }}</span>
                <span class="engine-cost" :class="activeGroup.badgeClass">{{ getEngineCost(item) }}x</span>

                <span
                  class="engine-info"
                  @mouseenter="showEngineTooltip(item, $event)"
                  @mouseleave="hideEngineTooltip"
                  @click.stop="toggleEngineTooltip(item, $event)"
                >
                  <el-icon>
                    <QuestionFilled />
                  </el-icon>
                </span>

              </button>
            </div>

            <div v-if="!activeGroup.items.length" class="engine-empty">{{ t('translate_engine_empty') }}</div>
          </div>

          <div class="translate-engine-footer">
            <span class="footer-cost-pill" :class="activeGroup.badgeClass">
              {{ getEngineCharCostText(activeGroup.multiplier) }} 
            </span>
            <span v-if="showNoOcrRestriction" class="footer-restriction-pill">
              {{ t('translate_engine_no_image_translation') }}
            </span>
            <!-- <button class="footer-help" type="button" @click="emit('help')">
              {{ t('translate_engine_help') }}
            </button> -->
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="engineTooltip.visible"
      class="translate-engine-tooltip"
      :style="engineTooltip.style"
      @mouseenter="onEngineTooltipMouseEnter"
      @mouseleave="onEngineTooltipMouseLeave"
    >
      <div class="engine-info-content">
        <div class="engine-info-desc">{{ engineTooltip.desc }}</div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { Close, QuestionFilled } from '@element-plus/icons-vue'
import { storeToRefs } from 'pinia'
import Toast from '@/utils/toast'
import useUserStore from '@/store/modules/user'

type EngineItem = Record<string, any>

interface EngineGroup {
  key: string
  label: string
  multiplier: string
  badgeClass: string
  isBeta: boolean
  order: number
  items: EngineItem[]
}

const props = withDefaults(defineProps<{
  modelValue: boolean
  currentEngineId?: number | string | null
  engineList?: EngineItem[] | Record<string, EngineItem> | null
  anchorRect?: DOMRect | null
  width?: string | number
}>(), {
  currentEngineId: null,
  engineList: null,
  anchorRect: null,
  // PC 默认让宽度由内容撑开（'auto'），移动端始终走 100% 抽屉，不受此值影响。
  // 传入显式数字/字符串可强制固定宽度。
  width: 'auto',
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
  (e: 'select', item: EngineItem): void
  (e: 'close'): void
  (e: 'help'): void
}>()

const { t, te } = useI18n()
const userStore = useUserStore()
const { translateEngines } = storeToRefs(userStore)
const viewportWidth = ref(1280)
const viewportHeight = ref(800)
const bodyOverflow = ref('')
const activeTab = ref('1')
const tabsRef = ref<HTMLElement | null>(null)
const engineTooltipHideTimer = ref<number | null>(null)
const engineTooltip = reactive({
  visible: false,
  hovered: false,
  cost: '--',
  desc: '',
  style: {
    position: 'fixed',
    top: '0px',
    left: '0px',
    zIndex: 12050,
    visibility: 'hidden',
  } as Record<string, string | number>,
})
const tabDragState = reactive({
  pointerId: null as number | null,
  startX: 0,
  startY: 0,
  startScrollLeft: 0,
  dragging: false,
  suppressClick: false,
})

const panelStyle = computed(() => {
  const widthCss = typeof props.width === 'number' ? `${props.width}px` : props.width
  if (isMobile.value) {
    return {
      position: 'fixed',
      left: '0',
      right: '0',
      bottom: '0',
      width: '100%',
      maxHeight: '88vh',
    }
  }

  // PC：宽度为 'auto' 时由 CSS 内容撑开；定位时按一个保守估计宽度做边界 clamp，
  // 真实测量留给浏览器自身完成。
  const isAutoWidth = widthCss === 'auto' || widthCss === '' || widthCss === undefined
  const estimatedWidth = typeof props.width === 'number'
    ? props.width
    : (isAutoWidth ? Math.min(500, Math.max(320, viewportWidth.value - 32)) : 420)
  const maxWidthCss = `calc(100vw - 32px)`

  if (props.anchorRect) {
    const maxLeft = Math.max(16, viewportWidth.value - estimatedWidth - 16)
    const left = Math.max(16, Math.min(Math.round(props.anchorRect.left), maxLeft))
    const top = Math.max(16, Math.min(props.anchorRect.bottom + 12, viewportHeight.value - 300))
    const maxHeight = Math.max(280, viewportHeight.value - top - 16)
    return {
      position: 'fixed',
      top: `${top}px`,
      left: `${left}px`,
      width: widthCss,
      maxWidth: maxWidthCss,
      maxHeight: `${maxHeight}px`,
    }
  }

  return {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: widthCss,
    maxWidth: maxWidthCss,
    maxHeight: '80vh',
  }
})

const dialogVisible = computed(() => props.modelValue)
const isMobile = computed(() => viewportWidth.value <= 768)
const engineGroups = computed(() => buildEngineGroups())
const activeGroup = computed(() => engineGroups.value.find(group => group.key === activeTab.value) || engineGroups.value[0])
const showNoOcrRestriction = computed(() => activeGroup.value.key === '0')

function syncViewportWidth() {
  if (!import.meta.client) {
    return
  }
  viewportWidth.value = window.innerWidth
  viewportHeight.value = window.innerHeight
}

function onTabsWheel(event: WheelEvent) {
  const container = tabsRef.value
  if (!container || container.scrollWidth <= container.clientWidth) {
    return
  }

  const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY
  if (!delta) {
    return
  }

  container.scrollLeft += delta
}

function positionEngineTooltip(target: HTMLElement) {
  const rect = target.getBoundingClientRect()
  const gap = 8
  const tooltipWidth = 280
  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight

  let top = rect.top - 8
  let left = rect.right + gap

  if (left + tooltipWidth > viewportWidth - 12) {
    left = rect.left - tooltipWidth - gap
  }

  if (left < 12) {
    left = Math.max(12, viewportWidth - tooltipWidth - 12)
  }

  const tooltipHeight = 84
  if (top + tooltipHeight > viewportHeight - 12) {
    top = viewportHeight - tooltipHeight - 12
  }

  if (top < 12) {
    top = 12
  }

  engineTooltip.style = {
    position: 'fixed',
    top: `${top}px`,
    left: `${left}px`,
    zIndex: 12050,
    visibility: 'visible',
  }
}

function showEngineTooltip(item: EngineItem, event: MouseEvent) {
  if (engineTooltipHideTimer.value !== null) {
    window.clearTimeout(engineTooltipHideTimer.value)
    engineTooltipHideTimer.value = null
  }
  engineTooltip.cost = getEngineCost(item)
  engineTooltip.desc = getEngineDescription(item)
  engineTooltip.visible = true
  positionEngineTooltip(event.currentTarget as HTMLElement)
}

function toggleEngineTooltip(item: EngineItem, event: MouseEvent) {
  if (engineTooltip.visible && !engineTooltip.hovered) {
    hideEngineTooltip()
    return
  }
  showEngineTooltip(item, event)
}

function onEngineTooltipMouseEnter() {
  engineTooltip.hovered = true
  if (engineTooltipHideTimer.value !== null) {
    window.clearTimeout(engineTooltipHideTimer.value)
    engineTooltipHideTimer.value = null
  }
}

function onEngineTooltipMouseLeave() {
  engineTooltip.hovered = false
  hideEngineTooltip()
}

function hideEngineTooltip() {
  if (engineTooltipHideTimer.value !== null) {
    window.clearTimeout(engineTooltipHideTimer.value)
  }
  engineTooltipHideTimer.value = window.setTimeout(() => {
    if (engineTooltip.hovered) {
      return
    }
    engineTooltip.visible = false
    engineTooltip.style = {
      position: 'fixed',
      top: '0px',
      left: '0px',
      zIndex: 12050,
      visibility: 'hidden',
    }
  }, 80)
}

function scrollActiveTabIntoView() {
  const container = tabsRef.value
  if (!container) {
    return
  }

  const activeTabEl = container.querySelector<HTMLElement>(`[data-tab-key="${activeTab.value}"]`)
  if (!activeTabEl) {
    return
  }

  const padding = 8
  const left = activeTabEl.offsetLeft - padding
  const right = activeTabEl.offsetLeft + activeTabEl.offsetWidth + padding
  const viewLeft = container.scrollLeft
  const viewRight = viewLeft + container.clientWidth

  if (left < viewLeft) {
    container.scrollLeft = Math.max(0, left)
  } else if (right > viewRight) {
    container.scrollLeft = right - container.clientWidth
  }
}

function onTabClick(key: string, event: MouseEvent) {
  if (tabDragState.suppressClick) {
    event.preventDefault()
    event.stopPropagation()
    return
  }
  activeTab.value = key
}

function onTabsPointerDown(event: PointerEvent) {
  const container = tabsRef.value
  if (!container || event.button !== 0 || container.scrollWidth <= container.clientWidth) {
    return
  }

  tabDragState.pointerId = event.pointerId
  tabDragState.startX = event.clientX
  tabDragState.startY = event.clientY
  tabDragState.startScrollLeft = container.scrollLeft
  tabDragState.dragging = false
}

function onTabsPointerMove(event: PointerEvent) {
  const container = tabsRef.value
  if (!container || tabDragState.pointerId !== event.pointerId) {
    return
  }

  const deltaX = event.clientX - tabDragState.startX
  const deltaY = event.clientY - tabDragState.startY
  if (!tabDragState.dragging && Math.abs(deltaX) < 4 && Math.abs(deltaY) < 4) {
    return
  }

  tabDragState.dragging = true
  tabDragState.suppressClick = true
  container.scrollLeft = tabDragState.startScrollLeft - deltaX
  event.preventDefault()
}

function endTabsPointerDrag() {
  if (tabDragState.pointerId === null) {
    return
  }

  tabDragState.pointerId = null
  if (tabDragState.dragging) {
    window.setTimeout(() => {
      tabDragState.suppressClick = false
    }, 0)
  }
  tabDragState.dragging = false
}

function onTabsPointerUp(event: PointerEvent) {
  if (tabDragState.pointerId !== event.pointerId) {
    return
  }
  endTabsPointerDrag()
}

function onTabsPointerCancel(event: PointerEvent) {
  if (tabDragState.pointerId !== event.pointerId) {
    return
  }
  tabDragState.suppressClick = false
  endTabsPointerDrag()
}

function onTabsPointerLeave() {
  if (tabDragState.pointerId === null) {
    return
  }
  endTabsPointerDrag()
}

function closeDialog() {
  emit('update:modelValue', false)
  emit('close')
}

function normalizeEngineList() {
  const source = props.engineList ?? translateEngines.value ?? []
  return Array.isArray(source) ? source : Object.values(source)
}

function getEngineName(item: EngineItem) {
  return String(item.engineShowName ?? item.engineName ?? item.name ?? '')
}

function getEngineKey(item: EngineItem) {
  return String(item.engineId ?? item.engineName ?? item.engineShowName ?? getEngineName(item))
}

function getEngineCost(item: EngineItem) {
  return formatRatio(item.tokenCostRatio) || String(item.tokenCostRatio ?? '')
}

function getEngineCharCostText(multiplier: string) {
  return t('translate_engine_char_cost').replace('*', multiplier)
}

function getEngineDescription(item: EngineItem) {
  const key = String(item.engineDescribe ?? '').trim()
  if (!key) {
    return ''
  }

  // 描述文案优先找 `*_describe`，不存在时再兜底到 `*_describe1`。
  if (te(key)) {
    const desc = t(key)
    return desc
  }

  const fallbackKey = `${key}1`
  if (te(fallbackKey)) {
    return t(fallbackKey)
  }

  return ''
}

function getIconStyle(item: EngineItem) {
  return {
    backgroundColor: item.iconRgbValue || '#E5E7EB',
    color: '#fff',
  }
}

function getFallbackIcon(item: EngineItem) {
  const name = getEngineName(item)
  return name ? name.slice(0, 1) : 'D'
}

function isSelected(item: EngineItem) {
  if (props.currentEngineId === null || props.currentEngineId === undefined || props.currentEngineId === '') {
    return false
  }
  return String(item.engineId) === String(props.currentEngineId)
}

function isDisabled(item: EngineItem) {
  return Number(item.showFlag) === -1
}

// 档位倍率以接口下发的 tokenCostRatio 为准，这里只在某档位暂时没有引擎时兜底
const GROUP_FALLBACK_RATIO: Record<string, number> = {
  '0': 0.5,
  '1': 1,
  '2': 2,
  '3': 4,
  '4': 6,
}

// 2 -> "2"、2.00 -> "2"、0.5 -> "0.5"，避免徽章上出现 "2.00x"
function formatRatio(value: unknown) {
  const num = Number(value)
  if (!Number.isFinite(num) || num <= 0) {
    return ''
  }
  return String(Number(num.toFixed(2)))
}

// 档位徽章取该档位下引擎的最低倍率：同档倍率一致时就是该值，
// 混档时按最低值展示，各引擎的真实倍率仍由列表行单独显示。
function getGroupMultiplier(key: string, items: EngineItem[]) {
  const ratios = items
    .map(item => Number(item.tokenCostRatio))
    .filter(num => Number.isFinite(num) && num > 0)

  const ratio = ratios.length ? Math.min(...ratios) : (GROUP_FALLBACK_RATIO[key] ?? 1)
  return `${formatRatio(ratio)}x`
}

function getGroupMeta() {
  return [
    {
      key: '0',
      label: t('translate_engine_group_light'),
      badgeClass: 'is-green',
      isBeta: false,
      order: 0,
    },
    {
      key: '1',
      label: t('translate_engine_group_basic'),
      badgeClass: 'is-gray',
      isBeta: false,
      order: 1,
    },
    {
      key: '2',
      label: t('translate_engine_group_advanced'),
      badgeClass: 'is-blue',
      isBeta: false,
      order: 2,
    },
    {
      key: '3',
      label: t('translate_engine_group_expert'),
      badgeClass: 'is-amber',
      isBeta: false,
      order: 3,
    },
    {
      key: '4',
      label: t('translate_engine_group_reasoning'),
      badgeClass: 'is-red',
      isBeta: true,
      order: 4,
    },
  ] satisfies Omit<EngineGroup, 'items' | 'multiplier'>[]
}

function getEngineLevel(item: EngineItem) {
  if (item.engineLevel === null || item.engineLevel === undefined || item.engineLevel === '') {
    return '1'
  }
  const level = String(Number(item.engineLevel))
  return ['0', '1', '2', '3', '4'].includes(level) ? level : '1'
}

function buildEngineGroups(): EngineGroup[] {
  const groups = getGroupMeta().map(group => ({
    ...group,
    items: [] as EngineItem[],
  }))

  normalizeEngineList().forEach((item) => {
    const level = getEngineLevel(item)
    const target = groups.find(group => group.key === level) ?? groups[0]
    target.items.push(item)
  })

  const sortEngine = (a: EngineItem, b: EngineItem) => {
    const sortA = Number(a.sort ?? 0)
    const sortB = Number(b.sort ?? 0)
    if (sortA !== sortB) {
      return sortA - sortB
    }
    return String(getEngineName(a)).localeCompare(String(getEngineName(b)), 'zh-Hans-CN')
  }

  return groups
    .map(group => ({
      ...group,
      items: [...group.items].sort(sortEngine),
    }))
    .map(group => ({
      ...group,
      multiplier: getGroupMultiplier(group.key, group.items),
    }))
    .sort((a, b) => a.order - b.order)
}

function syncActiveTabToCurrent() {
  const currentEngine = normalizeEngineList().find(item => String(item.engineId) === String(props.currentEngineId))
  const currentLevel = currentEngine ? getEngineLevel(currentEngine) : '1'
  activeTab.value = currentLevel
}

function handleSelect(item: EngineItem) {
  if (isDisabled(item)) {
    Toast(t('engineMaintenanceProgress'))
    return
  }

  emit('select', item)
  closeDialog()
}

watch(dialogVisible, (visible) => {
  if (!import.meta.client) {
    return
  }
  if (visible) {
    bodyOverflow.value = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    syncViewportWidth()
    syncActiveTabToCurrent()
    nextTick(scrollActiveTabIntoView)
  } else {
    document.body.style.overflow = bodyOverflow.value
    hideEngineTooltip()
  }
}, { immediate: true })

watch(() => props.currentEngineId, () => {
  if (dialogVisible.value) {
    syncActiveTabToCurrent()
  }
})

watch(
  () => translateEngines.value,
  () => {
    if (dialogVisible.value) {
      syncActiveTabToCurrent()
      nextTick(scrollActiveTabIntoView)
    }
  },
  { deep: true }
)

watch(activeTab, () => {
  if (dialogVisible.value) {
    hideEngineTooltip()
    nextTick(scrollActiveTabIntoView)
  }
})

onMounted(() => {
  syncViewportWidth()
  if (!import.meta.client) {
    return
  }
  window.addEventListener('resize', syncViewportWidth, { passive: true })
})

onBeforeUnmount(() => {
  if (import.meta.client) {
    window.removeEventListener('resize', syncViewportWidth)
    document.body.style.overflow = bodyOverflow.value
    hideEngineTooltip()
  }
})
</script>

<style scoped src="./TranslateEngine.less" lang="less"></style>
